import React, { useRef } from 'react';
import { Icon } from '@amzn/awsui-components-react-v3';
import './InfoTooltip.scss';

interface InfoTooltipProps {
  /** The prose to reveal. A full sentence — this is the one thing in a panel meant to be read, not scanned. */
  text: string;
  /**
   * What the tooltip is about, in the words already on screen beside it. Becomes the trigger's accessible
   * name ("About Crawler Fetcher"): a panel has a dozen of these, and "more information" a dozen times over
   * tells a screen-reader user nothing about which one they have landed on.
   */
  about: string;
}

/**
 * Sequence for the bubble ids that tie a trigger to its own prose. A counter rather than `useId`, which
 * React 16 does not have, and rather than deriving the id from `about` — two capabilities could share a
 * display name, and an id collision would silently point every duplicate trigger at the first bubble.
 */
let sequence = 0;

/**
 * The panels' one tooltip: the console's info icon, revealing a line of prose on hover or focus.
 *
 * Hand-rolled rather than Cloudscape's `Popover`, which was tried first. Inside the loss badge its trigger
 * brought an inline-block wrapper and link colouring that broke the badge's single line, and its panel
 * chrome looked nothing like the rest of this folder — so the panel had two kinds of hover that behaved
 * differently, on a screen where the reader learns the convention from whichever they try first. This is
 * one component with one stylesheet, used for every description the funnel shows.
 *
 * Shown by CSS, not by state. The bubble is always in the DOM and revealed by `:hover`/`:focus` on the
 * trigger, which means no re-render per hover, no timers, and — the reason that matters — the prose stays in
 * the accessibility tree, so `aria-describedby` resolves for a reader who never hovers anything. It is
 * faded out with `opacity` rather than `visibility: hidden` for the same reason: hidden content is pruned
 * from that tree, taking the description with it.
 *
 * The trigger is a real `button` because these sit on rows that are themselves click targets. That earns
 * keyboard focus, and the handlers below keep its own activation from reaching the row underneath.
 */
export const InfoTooltip: React.FC<InfoTooltipProps> = ({ text, about }) => {
  const idRef = useRef<string>();
  if (!idRef.current) {
    sequence += 1;
    idRef.current = `ovp-info-${sequence}`;
  }

  return (
    <span className="ovp-info">
      <button
        type="button"
        className="ovp-info__trigger"
        aria-label={`About ${about}`}
        aria-describedby={idRef.current}
        // Both stops are load-bearing, not defensive: the capability head line is inside a `role="button"`
        // that toggles the drill-down, and Enter and Space are that row's keys as well as this button's. A
        // reader asking what a stage does would otherwise expand or collapse it.
        onClick={(event) => event.stopPropagation()}
        onKeyDown={(event) => {
          event.stopPropagation();
          // Focus is what holds the bubble open, so blurring is what closes it — Escape is the key a reader
          // will reach for, and without this the prose stays up until they tab somewhere else.
          if (event.key === 'Escape') {
            event.currentTarget.blur();
          }
        }}
      >
        {/* The design system's own info icon, not a letter drawn into a circle. Two earlier attempts made
            the mark by hand — a lower-case i inside a CSS ring — and both looked homemade next to the
            console's icons: a font's letterform in a border-radius circle cannot match artwork drawn on the
            same 16px grid with the same 2px stroke as every other icon on the page, and at this size it read
            as a slash. `variant="normal"` resolves to `currentColor`, so the colour and the hover state stay
            with this stylesheet. The svg is `aria-hidden`; `aria-label` above is the accessible name. */}
        <Icon name="status-info" variant="normal" size="small" />
      </button>
      <span className="ovp-info__bubble" role="tooltip" id={idRef.current}>
        {text}
      </span>
    </span>
  );
};
